import { useEffect, useRef, useState } from "react";

import { reactionLabels } from "../content/productCopy";
import { useTrafficStore } from "../stores/useTrafficStore";
import { REACTIONS } from "../types/traffic";
import { Icon } from "./Icons";
import { LocationLabel } from "./LocationLabel";

function inputTypeForPointer(event: React.PointerEvent): "pointer" | "touch" {
  return event.pointerType === "touch" ? "touch" : "pointer";
}

export function TrafficHud() {
  const [shareMessage, setShareMessage] = useState("");
  const [secondaryActionsOpen, setSecondaryActionsOpen] = useState(false);
  const [reportOptionsOpen, setReportOptionsOpen] = useState(false);
  const [hiddenPlateIds, setHiddenPlateIds] = useState<Set<string>>(
    () => new Set(),
  );
  const phase = useTrafficStore((state) => state.phase);
  const count = useTrafficStore((state) => state.driverCount);
  const speed = useTrafficStore((state) => state.displayedSpeed);
  const trafficState = useTrafficStore((state) => state.trafficState);
  const remoteCount = useTrafficStore((state) => state.visibleRemoteCount);
  const remoteDrivers = useTrafficStore((state) => state.remoteDrivers);
  const connectionState = useTrafficStore((state) => state.connectionState);
  const realtimeMode = useTrafficStore((state) => state.realtimeMode);
  const transportMode = useTrafficStore((state) => state.transportMode);
  const isPrimaryTab = useTrafficStore((state) => state.isPrimaryTab);
  const sessionId = useTrafficStore((state) => state.sessionId);
  const cohortChannel = useTrafficStore((state) => state.cohortChannel);
  const soundEnabled = useTrafficStore((state) => state.soundEnabled);
  const reactionPickerOpen = useTrafficStore(
    (state) => state.reactionPickerOpen,
  );
  const selectedRemote = useTrafficStore((state) => state.selectedRemote);
  const lane = useTrafficStore((state) => state.lane);
  const elapsed = useTrafficStore((state) => state.elapsedSeconds);
  const distance = useTrafficStore((state) => state.distanceMiles);
  const changeLane = useTrafficStore((state) => state.changeLane);
  const honk = useTrafficStore((state) => state.honk);
  const flashLights = useTrafficStore((state) => state.flashLights);
  const toggleReactionPicker = useTrafficStore(
    (state) => state.toggleReactionPicker,
  );
  const react = useTrafficStore((state) => state.react);
  const inspectRemote = useTrafficStore((state) => state.inspectRemote);
  const setGarageOpen = useTrafficStore((state) => state.setGarageOpen);
  const setInfoOpen = useTrafficStore((state) => state.setInfoOpen);
  const toggleSound = useTrafficStore((state) => state.toggleSound);
  const takeExit = useTrafficStore((state) => state.takeExit);
  const profile = useTrafficStore((state) => state.profile);
  const viewerLocation = useTrafficStore((state) => state.viewerLocation);
  const liveAnnouncement = useRef<HTMLParagraphElement>(null);
  const driving = phase === "driving" || phase === "exiting";
  const isMock = transportMode === "mock";
  const countAvailable = isMock || sessionId !== null;
  const driverLabel = count === 1 ? "driver" : "drivers";
  const connectionLabel = !isPrimaryTab
    ? "MIRROR TAB"
    : isMock
      ? "MOCK TRAFFIC"
      : realtimeMode === "aggregate"
        ? "AGGREGATE"
        : realtimeMode === "offline" || transportMode === "offline"
          ? "RADIO OFFLINE"
          : connectionState === "connected"
            ? "RADIO LIVE"
            : connectionState === "connecting" || phase === "merging"
              ? "TUNING RADIO"
              : !sessionId && (phase === "spectating" || phase === "report")
                ? "RADIO READY"
                : "RADIO OFFLINE";

  const shareTraffic = async () => {
    const text = `I’m in traffic with ${count.toLocaleString()} drivers. Come make it worse.`;
    const share = (
      navigator as {
        share?: (data: ShareData) => Promise<void>;
      }
    ).share;
    try {
      if (share) {
        await share.call(navigator, {
          title: "The Internet Traffic Jam",
          text,
          url: location.origin,
        });
        setShareMessage("Traffic invitation shared.");
      } else {
        await navigator.clipboard.writeText(`${text} ${location.origin}`);
        setShareMessage("Traffic invitation copied.");
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        setShareMessage("Sharing unavailable.");
      }
    }
  };

  const reportPlate = (
    reason: "offensive" | "impersonation" | "contact_info" | "reserved",
  ) => {
    if (!selectedRemote) return;
    const publicDriverId = selectedRemote.publicDriverId;
    setHiddenPlateIds((current) => new Set(current).add(publicDriverId));
    setReportOptionsOpen(false);
    inspectRemote(null);
    void fetch("/api/moderation/report", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ targetPublicDriverId: publicDriverId, reason }),
    }).catch(() => undefined);
  };

  useEffect(() => {
    if (!driving) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLButtonElement
      ) {
        return;
      }
      if (event.key === "ArrowLeft" || event.key.toLowerCase() === "a") {
        event.preventDefault();
        changeLane(-1, "keyboard");
      }
      if (event.key === "ArrowRight" || event.key.toLowerCase() === "d") {
        event.preventDefault();
        changeLane(1, "keyboard");
      }
      if (event.code === "Space") {
        event.preventDefault();
        honk();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [changeLane, driving, honk]);

  return (
    <>
      <header className="top-hud">
        <button
          className="wordmark"
          type="button"
          onClick={() => setInfoOpen(true)}
          aria-label="About The Internet Traffic Jam"
        >
          <span>THE INTERNET</span>
          <strong>TRAFFIC JAM</strong>
        </button>

        <div
          className="traffic-readout"
          aria-label={`${countAvailable ? `${count} ${driverLabel}` : "Driver count unavailable"}, ${trafficState}, ${Math.round(speed)} miles per hour${profile ? `, plate ${profile.plate}` : ""}`}
        >
          <div className="traffic-readout__count">
            <strong>{countAvailable ? count.toLocaleString() : "—"}</strong>
            <span>{count === 1 ? "DRIVER" : "DRIVERS"}</span>
          </div>
          <div
            className={`traffic-readout__state state--${trafficState.toLowerCase().replaceAll(" ", "-")}`}
          >
            <span>{trafficState}</span>
            <strong>
              {Math.round(speed)} <small>MPH</small>
            </strong>
          </div>
          {profile ? (
            <div className="traffic-readout__plate">
              <strong>{profile.plate}</strong>
              {viewerLocation ? (
                <LocationLabel
                  location={viewerLocation}
                  className="traffic-readout__location"
                  compact
                />
              ) : (
                <span>PLATE</span>
              )}
            </div>
          ) : null}
        </div>

        <nav className="utility-controls" aria-label="Experience settings">
          <span
            className={`connection-pill${connectionLabel === "RADIO LIVE" ? " connection-pill--live" : ""}`}
          >
            <span className="live-dot" aria-hidden="true" />
            <span>{connectionLabel}</span>
          </span>
          <button
            className="icon-button"
            type="button"
            aria-label={soundEnabled ? "Mute sound" : "Enable sound"}
            aria-pressed={soundEnabled}
            onClick={() => void toggleSound()}
          >
            <Icon name={soundEnabled ? "sound" : "muted"} />
          </button>
          <button
            className="icon-button utility-info"
            type="button"
            aria-label="How this works"
            onClick={() => setInfoOpen(true)}
          >
            <Icon name="info" />
          </button>
        </nav>
      </header>

      {driving ? (
        <>
          <div className="session-strip" aria-label="Current trip">
            <span>LANE {lane}</span>
            <span>
              {Math.floor(elapsed / 60)}:
              {String(Math.floor(elapsed % 60)).padStart(2, "0")}
            </span>
            <span>{distance.toFixed(2)} MI</span>
            <span>
              {!isPrimaryTab
                ? "MIRROR — SAME CAR"
                : countAvailable && remoteCount > 0 && count > remoteCount + 1
                  ? `SHOWING ${remoteCount} NEARBY`
                  : `${remoteCount} NEARBY`}
            </span>
            {cohortChannel ? (
              <span>ROAD {cohortChannel.slice(-4).toUpperCase()}</span>
            ) : null}
          </div>

          <div className="lane-controls" aria-label="Lane controls">
            <button
              type="button"
              aria-label="Move one lane left"
              onPointerDown={(event) =>
                changeLane(-1, inputTypeForPointer(event))
              }
              onClick={(event) => {
                if (event.detail === 0) changeLane(-1, "keyboard");
              }}
              disabled={phase === "exiting"}
            >
              <Icon name="arrow-left" />
            </button>
            <span>CHANGE LANE</span>
            <button
              type="button"
              aria-label="Move one lane right"
              onPointerDown={(event) =>
                changeLane(1, inputTypeForPointer(event))
              }
              onClick={(event) => {
                if (event.detail === 0) changeLane(1, "keyboard");
              }}
              disabled={phase === "exiting"}
            >
              <Icon name="arrow-right" />
            </button>
          </div>

          <nav className="drive-controls" aria-label="Driving controls">
            <button
              className="drive-control drive-control--secondary"
              type="button"
              onClick={() => setGarageOpen(true)}
              disabled={phase === "exiting"}
            >
              <Icon name="garage" />
              <span>Garage</span>
            </button>
            <button
              className="drive-control"
              type="button"
              onClick={flashLights}
              disabled={phase === "exiting"}
            >
              <Icon name="lights" />
              <span>Lights</span>
            </button>
            <button
              className="drive-control drive-control--horn"
              type="button"
              onClick={honk}
              disabled={phase === "exiting"}
            >
              <Icon name="horn" />
              <span>Honk</span>
            </button>
            <div className="reaction-control">
              {reactionPickerOpen ? (
                <div className="reaction-picker" aria-label="Choose a reaction">
                  {REACTIONS.map((reaction) => (
                    <button
                      key={reaction}
                      type="button"
                      onClick={() => react(reaction)}
                    >
                      <span aria-hidden="true">
                        {reaction === "wave"
                          ? "HI"
                          : reaction === "sigh"
                            ? "…"
                            : reaction === "coffee"
                              ? "CUP"
                              : "SLOW"}
                      </span>
                      {reactionLabels[reaction]}
                    </button>
                  ))}
                </div>
              ) : null}
              <button
                className="drive-control"
                type="button"
                aria-expanded={reactionPickerOpen}
                onClick={toggleReactionPicker}
                disabled={phase === "exiting"}
              >
                <Icon name="reaction" />
                <span>React</span>
              </button>
            </div>
            <button
              className="drive-control drive-control--share"
              type="button"
              onClick={() => void shareTraffic()}
              disabled={phase === "exiting"}
            >
              <Icon name="share" />
              <span>Share</span>
            </button>
            <button
              className="drive-control drive-control--exit"
              type="button"
              onClick={() => {
                setSecondaryActionsOpen(false);
                takeExit();
              }}
              disabled={phase === "exiting"}
            >
              <Icon name="exit" />
              <span>{phase === "exiting" ? "Exiting…" : "Take Exit"}</span>
            </button>
            <div className="secondary-actions">
              {secondaryActionsOpen ? (
                <div
                  className="secondary-actions__menu"
                  aria-label="More actions"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setGarageOpen(true);
                      setSecondaryActionsOpen(false);
                    }}
                  >
                    <Icon name="garage" />
                    Garage
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void shareTraffic();
                      setSecondaryActionsOpen(false);
                    }}
                  >
                    <Icon name="share" />
                    Share
                  </button>
                </div>
              ) : null}
              <button
                className="drive-control drive-control--more"
                type="button"
                aria-expanded={secondaryActionsOpen}
                onClick={() => setSecondaryActionsOpen((open) => !open)}
                disabled={phase === "exiting"}
              >
                <Icon name="more" />
                <span>More</span>
              </button>
            </div>
          </nav>
        </>
      ) : null}

      {selectedRemote ? (
        <aside
          className="plate-card"
          aria-label={`Driver ${selectedRemote.plate}`}
        >
          <button
            type="button"
            aria-label="Close driver details"
            onClick={() => {
              setReportOptionsOpen(false);
              inspectRemote(null);
            }}
          >
            <Icon name="close" />
          </button>
          <span className="plate-card__eyebrow">DRIVING NOW</span>
          <strong>
            {hiddenPlateIds.has(selectedRemote.publicDriverId)
              ? "PLATE HIDDEN"
              : selectedRemote.plate}
          </strong>
          <LocationLabel
            location={selectedRemote.location}
            className="plate-card__location"
          />
          <span className="plate-card__meta">
            {selectedRemote.vehiclePreset.replace("-", " ")} ·{" "}
            {selectedRemote.colorId.replace("-", " ")}
          </span>
          <span className="plate-card__trip">
            {Math.max(0, Math.floor(selectedRemote.sessionElapsedSeconds / 60))}{" "}
            min this trip · {selectedRemote.sessionDistanceMiles.toFixed(1)} mi
          </span>
          {selectedRemote.badge ? (
            <em>
              {selectedRemote.badge === "founding"
                ? "FOUNDING DRIVER"
                : "GARAGE PACK"}
            </em>
          ) : null}
          {selectedRemote.badge && transportMode === "supabase" ? (
            reportOptionsOpen ? (
              <div className="plate-card__report-options">
                <button type="button" onClick={() => reportPlate("offensive")}>
                  Offensive
                </button>
                <button
                  type="button"
                  onClick={() => reportPlate("impersonation")}
                >
                  Impersonation
                </button>
                <button
                  type="button"
                  onClick={() => reportPlate("contact_info")}
                >
                  Contact info
                </button>
                <button type="button" onClick={() => reportPlate("reserved")}>
                  Reserved term
                </button>
              </div>
            ) : (
              <button
                className="plate-card__report"
                type="button"
                onClick={() => setReportOptionsOpen(true)}
              >
                Report plate
              </button>
            )
          ) : null}
        </aside>
      ) : null}

      <p ref={liveAnnouncement} className="sr-only" aria-live="polite">
        {countAvailable
          ? `${count} ${driverLabel}.`
          : "Driver count unavailable."}{" "}
        {trafficState}. {Math.round(speed)} miles per hour. {remoteCount} nearby
        drivers.
        {shareMessage}
      </p>
      <div className="sr-only" aria-label="Nearby driver state">
        {remoteDrivers
          .filter((driver) => !driver.departing)
          .map((driver) => (
            <span
              key={driver.sessionId}
              data-testid="remote-driver"
              data-session-id={driver.sessionId}
              data-lane={driver.lane}
              data-vehicle={driver.vehiclePreset}
              data-color={driver.colorId}
            >
              {driver.plate}
            </span>
          ))}
      </div>
    </>
  );
}
